/**
 * Everything eTMF is for, as a catalogue a customer reads, with the product set beside the
 * other trial master file products at the end.
 *
 * THE CARDS AND THEIR COPY LIVE IN `src/data/capabilities.tsx`, the comparison in
 * `src/data/featureMatrix.ts`, because the example pages and the tests need the same lists.
 * Taken from Trace's page (t12), itself modelled on t4's, so the suite has one way of saying what a
 * product does. Ed, 2026-11-03: "Lay down cres-aws-t13/cres-t13 and author the capabilities page."
 *
 * READABLE WITHOUT SIGNING IN. The reader is somebody deciding whether to evaluate this, and
 * asking them to create an account before they can see what a feature does is asking them to
 * take the card on trust. The splash links here.
 *
 * AN AVAILABLE CARD LINKS TO A WORKED EXAMPLE and a planned one does not.
 */
import React, {useState} from "react";
import {Box, Chip, Collapse, FormControlLabel, Paper, Switch, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";

import {EXAMPLE_BY_SLUG} from "../data/capabilityExamples";
import {CATALOGUE, NEXT_ADVANCES, NEXT_COMPLETES, NEXT_OUTLINED, STATUS_LABEL as LABEL} from "../data/capabilities";
import {White, Blue3, Grey2, Grey3, Grey5, Grey7, Grey17} from "../theme/styles";
import {ProductName} from "../data/product";
import {CONTEST_LABEL, LEVEL_LABEL, PRODUCTS, SOURCES, VISIBLE_MATRIX, contestOf} from "../data/featureMatrix";

// SPELT OUT, BECAUSE THE SENTENCE READS AS PROSE. "39 of these are built" in a paragraph of
// words looks like a typo; "Thirty-nine" does not.
const WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
               "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
               "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

function spell(value: number): string {
    if (value < 20) return WORDS[value];
    if (value < 100) {
        const unit = value % 10;
        return TENS[Math.floor(value / 10)] + (unit ? `-${WORDS[unit]}` : "");
    }
    return String(value);
}

const ALL = CATALOGUE.flatMap(group => group.capabilities);
const HAS_EXAMPLE = new Set(Object.keys(EXAMPLE_BY_SLUG));
const BUILT_COUNT = spell(ALL.filter(one => one.status === "built").length).replace(/^./, first => first.toUpperCase());
const PLANNED_COUNT = spell(ALL.filter(one => one.status !== "built").length);
const ROADMAP_ITEM_COUNT = spell(NEXT_COMPLETES.length + NEXT_ADVANCES.length);

/** Muted on purpose: an orientation aid for somebody scanning the column, not a scoreboard. */
const CONTEST_COLOUR: Record<string, string> = {
    contested: "#0b6fa4", ceded: "#6b7280", behind: "#8a6d1f", ahead: "#1E8A4B", neither: "#9aa3ad",
    planned: "#d32f2f",
};

/** THE ROADMAP PREVIEW MARK: an outline, not a border, so nothing re-wraps when it is on. */
const NEXT_MARK = {outline: "3px solid #d32f2f", outlineOffset: "0px"};

const CELL = {verticalAlign: "top" as const, padding: "8px 6px", borderBottom: `1px solid ${Grey2}`};

/** NO RED FOR A "NO", ANYWHERE: half the Nos on this page are our own and one is a decision. */
const LEVEL_STYLE: Record<string, object> = {
    yes: {backgroundColor: "#E6F4EA", color: "#137333"},
    partial: {backgroundColor: "#FEF7E0", color: "#8A6D00"},
    no: {backgroundColor: "transparent", border: `1px solid ${Grey3}`, color: Grey5},
    unknown: {backgroundColor: "transparent", border: `1px dashed ${Grey3}`, color: Grey5},
};

const h2 = {fontSize: {xs: 20, md: 24}, fontWeight: 600, color: Grey17};
const panel = {mt: 4, p: 3, border: `1px solid ${Grey3}`, borderRadius: 1, backgroundColor: Grey2, width: "100%", boxSizing: "border-box" as const};

export default function Capabilities() {
    const [showNext, setShowNext] = useState(false);

    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100%", width: "100%",
                  backgroundColor: White, boxSizing: "border-box", px: {xs: 2.5, md: 4}, py: {xs: 6, md: 8}}}>
            <Box sx={{width: "100%", maxWidth: "960px"}}>
                <Typography component="h1" sx={{fontSize: {xs: 22, sm: 29, md: 40}, fontWeight: 600, color: Grey17, lineHeight: 1.15, whiteSpace: {md: "nowrap"}}}>
                    <ProductName/> Capabilities Guide
                </Typography>
                <Typography sx={{fontSize: {xs: 15, md: 17}, color: Grey7, mt: 4, lineHeight: 1.6}}>
                    Every capability <ProductName/> intends to have. eTMF is the sponsor&rsquo;s trial master file and
                    the site files it exchanges documents with: study, country and site folders on the TMF Reference
                    Model, the documents each should hold, quality review, expiry, delegation logs and the access an
                    inspector needs, with every document a controlled record signed under Part 11. It is sold on its
                    own, and for a customer who also runs Foundation it is one system rather than two. Each card says
                    whether it is available today or on the roadmap, and every card opens a worked example on one
                    running case, a pivotal study of an infusion pump at twelve sites; an example of a roadmap card
                    says so at the top. At the end, eTMF is set beside other trial master file products, with our own
                    gaps in the same table.
                </Typography>

                <Box sx={{...panel, mt: 5}}>
                    <Typography sx={{fontSize: 14, color: Grey17, lineHeight: 1.7}}>
                        <strong>{BUILT_COUNT} of these are built; {PLANNED_COUNT} are not.</strong> Every card says
                        where it stands, and &ldquo;Available&rdquo; means a customer could use it today through the
                        product, checked against what runs, not against a plan. eTMF was laid down on 3 November 2026
                        and nothing in it is built yet: the whole catalogue is the roadmap, and the red outline below
                        marks the first phase.
                    </Typography>
                </Box>

                {NEXT_COMPLETES.length === 0 && NEXT_ADVANCES.length === 0 ? null : (
                    <Box sx={{mt: 6, p: 4, border: `1px solid ${Grey2}`, borderRadius: "6px"}}>
                        <FormControlLabel
                            control={<Switch size="small" checked={showNext} onChange={event => setShowNext(event.target.checked)}/>}
                            label={<Typography sx={{fontSize: 13.5, fontWeight: 600, color: Grey17}}>
                                Preview: what the next {ROADMAP_ITEM_COUNT} roadmap items complete
                            </Typography>}/>
                        <Collapse in={showNext}>
                            <Typography sx={{fontSize: 12.5, color: Grey7, mt: 2, lineHeight: 1.65}}>
                                Outlined in red below: <b>{NEXT_COMPLETES.length} cards move to Available</b> when these are done.
                            </Typography>
                            <Box component="ul" sx={{m: 0, mt: 2, pl: 5}}>
                                {NEXT_COMPLETES.map(one => (
                                    <Box component="li" key={one.slug} sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.7}}>
                                        {one.item}{one.note ? <Typography component="span" sx={{fontSize: 12, color: Grey5}}>{" — "}{one.note}</Typography> : null}
                                    </Box>
                                ))}
                            </Box>
                            {NEXT_ADVANCES.map(one => (
                                <Typography key={one.slug} sx={{fontSize: 12.5, color: Grey7, mt: 3, lineHeight: 1.65}}>
                                    <b>Not outlined:</b> {one.item}. {one.note}
                                </Typography>
                            ))}
                        </Collapse>
                    </Box>
                )}

                {CATALOGUE.map(group => (
                    <Box key={group.title} sx={{mt: {xs: 8, md: 11}}}>
                        <Typography component="h2" sx={h2}>{group.title}</Typography>
                        <Typography sx={{fontSize: 13, color: Grey5, mt: 1}}>{group.blurb}</Typography>
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 4, mt: 5}}>
                            {group.capabilities.map(capability => (
                                <Paper key={capability.slug} variant="outlined"
                                       {...(HAS_EXAMPLE.has(capability.slug) ? {component: RouterLink, to: `/capabilities/${capability.slug}`} : {})}
                                       sx={{
                                           flex: "1 1 260px", minWidth: 0, p: 5, boxSizing: "border-box", borderColor: Grey3,
                                           backgroundColor: capability.status === "built" ? Grey2 : White, boxShadow: "none",
                                           display: "block", textDecoration: "none",
                                           ...(showNext && NEXT_OUTLINED.has(capability.slug) ? NEXT_MARK : {}),
                                           ...(HAS_EXAMPLE.has(capability.slug) ? {
                                               cursor: "pointer", transition: "border-color 120ms, box-shadow 120ms",
                                               "&:hover": {borderColor: Blue3, boxShadow: "0 1px 6px rgba(20,152,216,0.14)"},
                                           } : {}),
                                       }}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                        <Box sx={{color: capability.status === "built" ? Blue3 : Grey5, display: "flex"}}>{capability.icon}</Box>
                                        <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey17, flexGrow: 1}}>{capability.name}</Typography>
                                    </Box>
                                    <Typography sx={{fontSize: 13, color: Grey7, mt: 2, lineHeight: 1.6}}>{capability.text}</Typography>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mt: 3, flexWrap: "wrap"}}>
                                        <Chip label={LABEL[capability.status]} size="small" sx={{
                                            height: 20, fontSize: 11,
                                            backgroundColor: capability.status === "built" ? "#E6F4EA" : capability.status === "building" ? "#FEF7E0" : "transparent",
                                            border: capability.status === "planned" ? `1px solid ${Grey3}` : "none",
                                            color: capability.status === "built" ? "#137333" : capability.status === "building" ? "#8A6D00" : Grey5,
                                        }}/>
                                        {HAS_EXAMPLE.has(capability.slug) ? (
                                            <Typography sx={{fontSize: 11.5, color: Blue3}}>
                                                {capability.status === "built" ? "See an example" : "How it will work"} &rarr;
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                ))}

                {/* eTMF BESIDE THREE PRODUCTS, 2026-11-03. Our column is last,
                    so a reader arrives at it having read what the others do, which is the only
                    order the argument survives in if it is true. */}
                {VISIBLE_MATRIX().length === 0 ? null : (
                    <Box sx={{mt: {xs: 8, md: 11}}}>
                        <Typography component="h2" sx={h2}>How it compares</Typography>
                        <Typography sx={{fontSize: 13, color: Grey5, mt: 1}}>
                            Florence, Veeva Vault eTMF, Montrium eTMF Connect, and this product, feature by feature.
                        </Typography>
                        <Box sx={panel}>
                            <Typography sx={{fontSize: 14, color: Grey17, lineHeight: 1.7}}>
                                <strong>Fourteen rows, every competitor cell sourced from public pages, and our own column
                                honest: nothing of ours is built yet, so every cell of ours is a card on the roadmap.</strong>{" "}
                                Florence is the product a site is most likely to know: its site binders hold the
                                delegation logs, expiry dates and per-folder permissions a coordinator lives in, and its
                                eTMF pulls documents from them. Its public pages do not name a Reference Model version,
                                document states or an exchange format. Montrium names the model it follows and measures
                                completeness at every level; Veeva documents expected-document lists, classification and
                                quality sampling. eTMF is aimed at a device sponsor of a dozen sites: the model, the
                                expectations and the review a sponsor needs, the site logs a coordinator needs, and a
                                quality system that is there when wanted and absent when not.
                            </Typography>
                        </Box>

                        {VISIBLE_MATRIX().map(section => (
                            <Box key={section.title} sx={{mt: 6, width: "100%"}}>
                                <Typography sx={{fontSize: 16, fontWeight: 600, color: Grey17}}>{section.title}</Typography>
                                <Typography sx={{fontSize: 13, color: Grey7, mt: 0.75, lineHeight: 1.65}}>{section.blurb}</Typography>
                                <Box sx={{mt: 2, overflowX: "auto"}}>
                                    <Box component="table" sx={{width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 12.5}}>
                                        <Box component="thead">
                                            <Box component="tr">
                                                {["", ...PRODUCTS, "Parkour-AI"].map((heading, index) => (
                                                    <Box component="th" key={heading || index}
                                                         sx={{textAlign: "left", verticalAlign: "bottom", padding: "8px 10px", fontSize: 12, fontWeight: 600,
                                                              color: index === PRODUCTS.length + 1 ? Blue3 : Grey5, borderBottom: `1px solid ${Grey3}`,
                                                              width: index === 0 ? "19%" : `${81 / (PRODUCTS.length + 1)}%`}}>
                                                        {heading}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box component="tbody">
                                            {section.rows.map(row => (
                                                <Box component="tr" key={row.capability}>
                                                    <Box component="td" sx={CELL}>
                                                        <Typography component="span" sx={{display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: ".04em",
                                                                                           textTransform: "uppercase", mb: 0.5, color: CONTEST_COLOUR[contestOf(row)]}}>
                                                            {CONTEST_LABEL[contestOf(row)]}
                                                        </Typography>
                                                        <Typography sx={{fontSize: 13, fontWeight: 500, color: Grey17, lineHeight: 1.5}}>{row.capability}</Typography>
                                                        {row.note ? <Typography sx={{fontSize: 12, color: Grey5, mt: 0.5, lineHeight: 1.5}}>{row.note}</Typography> : null}
                                                    </Box>
                                                    {[...PRODUCTS.map(product => row.them[product]), row.ours].map((cell, index) => (
                                                        <Box component="td" key={index}
                                                             sx={{...CELL, backgroundColor: index === PRODUCTS.length ? "rgba(20,152,216,0.04)" : "transparent"}}>
                                                            <Chip label={LEVEL_LABEL[cell.level]} size="small" sx={{height: 19, fontSize: 11, ...LEVEL_STYLE[cell.level]}}/>
                                                            {cell.note ? <Typography sx={{fontSize: 12, color: Grey7, mt: 0.75, lineHeight: 1.5}}>{cell.note}</Typography> : null}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ))}

                        <Typography sx={{fontSize: 16, fontWeight: 600, color: Grey17, mt: 7}}>Where each column comes from</Typography>
                        {SOURCES.map(source => (
                            <Typography key={source.product} sx={{fontSize: 13, color: Grey7, mt: 1.5, lineHeight: 1.65}}>
                                <b>{source.product}</b>, {source.when}: {source.how}
                            </Typography>
                        ))}
                        <Typography sx={{fontSize: 13, color: Grey7, mt: 1.5, lineHeight: 1.65}}>
                            &ldquo;Not established&rdquo; means the reading did not settle it either way and is not a
                            polite No. Veeva&rsquo;s and Montrium&rsquo;s columns rest on one product page each, which is
                            why most of their cells say so. Nobody&rsquo;s product was signed into.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
